import { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { breadcrumbPageLabel, breadcrumbLabelI18nKey, pageDescriptionI18nKey } from "@/lib/navigationMeta";
import { usePageHeadingContext } from "./PageHeadingContext";

type AppPageHeadingProps = {
  className?: string;
};

/**
 * Wrapper that hides its children when the current route calls
 * `usePageHeadingOverride({ hidden: true })`. Used to hide the whole
 * breadcrumb bar (breadcrumb + title) on full-canvas pages.
 */
export function PageHeadingBar({ children }: { children: ReactNode }) {
  const { override } = usePageHeadingContext();
  if (override?.hidden) return null;
  return <>{children}</>;
}

/**
 * Global page title + description + divider under breadcrumbs (COMPLY.NOW shell).
 */
export function AppPageHeading({ className }: AppPageHeadingProps) {
  const { pathname } = useLocation();
  const { t, i18n } = useTranslation();
  const { override } = usePageHeadingContext();

  if (override?.hidden) {
    return null;
  }

  const labelKey = breadcrumbLabelI18nKey(pathname);
  const titleRaw =
    override?.title ??
    (labelKey
      ? t(`breadcrumbs.${labelKey}`, { defaultValue: breadcrumbPageLabel(pathname) })
      : breadcrumbPageLabel(pathname));
  const descKey = pageDescriptionI18nKey(pathname);
  const fallback = t("pageDescriptions.default");
  const descriptionRaw =
    override?.description ??
    t(`pageDescriptions.${descKey}`, {
      defaultValue: fallback,
    });
  const title = typeof titleRaw === "string" ? titleRaw : String(titleRaw ?? "");
  const description = typeof descriptionRaw === "string" ? descriptionRaw : String(descriptionRaw ?? "");

  const dir = i18n.dir();

  return (
    <div className={`main-page-heading ${className ?? ""}`.trim()} dir={dir}>
      <h1 className="main-page-heading__title">{title}</h1>
      {description ? <p className="main-page-heading__description">{description}</p> : null}
      <div className="main-page-heading__rule" role="presentation" />
    </div>
  );
}
